import { CollectionPopupComponent } from '../../../../shared';
import { voyageSpendErrorMessage } from '../voyage-credits.model';
import { AisMapFeature } from '../../shared/contracts/ais-map.feature';
import { AisMapContext } from '../../shared/contracts/ais-map.context';
import { VoyagePopupActions } from '../vessel-popup.types';
import { vrHost } from '../vr-host';

/**
 * What this feature must be TOLD rather than know (rule R5 / D1): both
 * callbacks are seams to the provider's wallet voice and the facade's
 * broadcast, in that order.
 */
export interface PortCallsFeatureOptions {
  /**
   * Re-push every price line after a wallet-moving response. The broadcast
   * is the facade's (`spend-broadcast.ts`): a balance that moved changes
   * EVERY feature's line, not just this one's.
   */
  syncCreditLines: () => void;
}

/**
 * PORT CALLS — the by-MMSI port-call history drawer's map-side half, as a
 * pluggable feature (phase 5, REFACTOR-PLAN.md §4.3/§7). Extracted verbatim
 * from `VoyageMapController`; one file, three contract hooks:
 *
 *   attach()       — the drawer's two callbacks (free stored lookup · paid fetch)
 *   syncCredits()  — this drawer's price line on every broadcast
 *   popupActions() — the anchor button on the shared vessel card
 *
 * PROVIDER-OWNED (decision D7): port calls are a Voyage Radar capability,
 * so this file lives in the provider's folder and speaks the provider's
 * API directly — no gateway indirection to hide a boundary that is not
 * being crossed. What stays SHARED is the contract it implements and the
 * drawer UI it drives; a provider without port calls simply mounts no
 * feature and lists no control, and nothing renders.
 */
export class PortCallsFeature extends AisMapFeature {
  /**
   * MMSI of the port-call fetch in flight, or null. Separate from the lookup
   * guard: the two are different purchases and one must not gate the
   * other, but a double-click on either must never buy the same thing twice.
   */
  private portCallLoadingMmsi: string | null = null;

  constructor(ctx: AisMapContext, private readonly opts: PortCallsFeatureOptions) {
    super(ctx);
  }

  /** What one port-call history costs on this provider, in its own units. */
  private price(): number {
    return this.ctx.spend?.priceFor('portCalls') ?? 0;
  }

  /**
   * Port calls. `onLookupStored` is the FREE half: the record already rode to
   * the client on the sensor document, so re-opening a bought history is a
   * lookup in memory rather than a request. The controller calls it itself on
   * every selection change, so there is no "open for this mmsi" method here.
   */
  override attach(): void {
    const ports = vrHost(this.ctx.host).portCalls;
    ports.onLookupStored = (mmsi) =>
      (this.ctx.sensor?.portCallHistory || []).find((r: any) => String(r?.mmsi) === mmsi) ??
      null;
    ports.onFetch = (mmsi, vesselName) => this.fetchPortCalls(mmsi, vesselName);
  }

  override syncCredits(remaining: number | null, spendingClosed: boolean): void {
    vrHost(this.ctx.host).portCalls.setCredits(remaining, this.price(), spendingClosed);
  }

  override popupActions(mmsi: string): Partial<VoyagePopupActions> {
    return {
      /**
       * Offered on EVERY hull, saved or not: port calls are a by-MMSI question
       * and any arrow on the chart is a legitimate subject. Gated on the
       * drawer being MOUNTED — the button does nothing but open a drawer, and
       * `openAndFetch` returns immediately when `onRequestOpen` is null — so a
       * card opened during a replay contributes null, which hides the button
       * (invariant I5) rather than shipping one that swallows the click.
       */
      portCallCredits: vrHost(this.ctx.host).portCalls.onRequestOpen ? this.price() : null,
    };
  }

  /**
   * The by-MMSI port-call history: every recorded arrival, departure
   * and stay for one hull. Modelled on the vessel lookup, which is
   * the reference implementation for a 1-credit client spend — every one of its
   * behaviours is load-bearing and repeated here.
   *
   * Stores nothing itself beyond mirroring the server's record onto the local
   * sensor doc: that mirror is what makes re-opening the drawer free, because
   * the controller's own selection watcher reads `portCallHistory` directly.
   */
  fetchPortCalls(mmsi: string, vesselName: string | null): boolean {
    const sensor = this.ctx.sensor;
    const taskid = this.ctx.host.collection?.taskId;
    const sensorId = sensor?.sensor_uuid ?? sensor?.id;
    const ports = vrHost(this.ctx.host).portCalls;
    if (!taskid || !sensorId || !mmsi) {
      ports.setFailed();
      return false;
    }
    // In-flight guard — a double click must not buy the same history twice.
    if (this.portCallLoadingMmsi) return false;

    // Refused at the door, before any request, and the drawer's spinner is
    // cleared so it does not hang on a fetch that never left.
    const refusal = this.ctx.spend ? this.ctx.spend.refuse('portCalls', this.price()) : null;
    if (refusal) {
      ports.setFailed();
      this.ctx.host.popupService.openPopup(CollectionPopupComponent, refusal, '', '40%');
      return false;
    }

    this.portCallLoadingMmsi = mmsi;
    this.ctx.host.apiService
      .getVoyageRadarPortCalls({ taskid, sensorId, mmsi, vesselName })
      .toPromise()
      .then((res: any) => {
        this.portCallLoadingMmsi = null;
        // The stored counter IS the balance — mirror it so every price line agrees.
        if (res?.apiCreditsRemaining != null && sensor) {
          sensor.apiCreditsRemaining = res.apiCreditsRemaining;
          this.opts.syncCreditLines();
        }
        if (!res?.record) {
          // An empty answer is still an ANSWER for this hull, and it was free.
          // Handing it to the controller is what stops the drawer re-offering a
          // spend that already happened for nothing.
          ports.setFetched(mmsi, { mmsi, calls: [], total: res?.total ?? 0, name: null });
          return;
        }
        // Mirror the record the server wrote into the local sensor doc, in the
        // same shape and under the same cap, so the panel and the drawer agree
        // without a refetch.
        const list: any[] = Array.isArray(sensor.portCallHistory) ? sensor.portCallHistory : [];
        const i = list.findIndex((r: any) => String(r?.mmsi) === mmsi);
        if (i >= 0) list[i] = res.record;
        else {
          list.push(res.record);
          if (list.length > 25) list.splice(0, list.length - 25);
        }
        sensor.portCallHistory = list;
        ports.setFetched(mmsi, res.record);
      })
      .catch((err: any) => {
        this.portCallLoadingMmsi = null;
        ports.setFailed();
        if (err?.error?.apiCreditsRemaining != null && sensor) {
          sensor.apiCreditsRemaining = err.error.apiCreditsRemaining;
          this.opts.syncCreditLines();
        }
        // Failure copy always states the charge outcome — silence here reads
        // as "it took my credit". One shared ladder, so this path and the
        // refresh/track/search ones cannot describe the same wallet differently.
        this.ctx.host.popupService.openPopup(
          CollectionPopupComponent,
          voyageSpendErrorMessage(err, 'portCalls', this.price()),
          '',
          '40%'
        );
      });
    return true;
  }
}
